import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, throwError, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface StickyNote {
  id: number;
  title?: string;
  content?: string;
  color: string;
  pos_x: number;
  pos_y: number;
  width: number;
  height: number;
  is_open: boolean;
  z_index: number;
  sequence: number;
}

interface NoteGeometry {
  pos_x: number;
  pos_y: number;
  width: number;
  height: number;
}

const FAB_POS_KEY = 'sticky-fab-pos'; // not user-scoped — just a screen position

@Injectable({ providedIn: 'root' })
export class StickyNotesService {
  private readonly url = `${environment.apiUrl}/sticky-notes`;
  private _notes = new BehaviorSubject<StickyNote[]>([]);
  notes$ = this._notes.asObservable();

  constructor(private http: HttpClient) {}

  // ── User ID helper ────────────────────────────────────────────────────────

  private currentUserId(): number | null {
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      return userData?.id ?? null;
    } catch {
      return null;
    }
  }

  // ── Scoped localStorage keys ──────────────────────────────────────────────

  private geoKey(userId: number): string {
    return `sticky-notes-geo-${userId}`;
  }

  private uiKey(userId: number): string {
    return `sticky-notes-ui-${userId}`;
  }

  // ── Geometry (localStorage only) ──────────────────────────────────────────

  saveGeometry(id: number, geo: Partial<NoteGeometry>): void {
    const uid = this.currentUserId();
    if (!uid) return;
    const all = this.loadAllGeometry();
    all[id] = { ...all[id], ...geo };
    localStorage.setItem(this.geoKey(uid), JSON.stringify(all));
    // Also update in-memory so reopening the window reads the latest position
    this._notes.next(
      this._notes.value.map(n => n.id === id ? { ...n, ...geo } : n)
    );
  }

  getGeometry(id: number): NoteGeometry | null {
    return this.loadAllGeometry()[id] ?? null;
  }

  private loadAllGeometry(): Record<number, NoteGeometry> {
    const uid = this.currentUserId();
    if (!uid) return {};
    try { return JSON.parse(localStorage.getItem(this.geoKey(uid)) ?? '{}'); }
    catch { return {}; }
  }

  private clearGeometry(id: number): void {
    const uid = this.currentUserId();
    if (!uid) return;
    const all = this.loadAllGeometry();
    delete all[id];
    localStorage.setItem(this.geoKey(uid), JSON.stringify(all));
  }

  // ── UI state (localStorage only — is_open) ────────────────────────────────

  private loadAllUiState(): Record<number, { is_open: boolean }> {
    const uid = this.currentUserId();
    if (!uid) return {};
    try { return JSON.parse(localStorage.getItem(this.uiKey(uid)) ?? '{}'); }
    catch { return {}; }
  }

  private saveUiState(id: number, state: { is_open: boolean }): void {
    const uid = this.currentUserId();
    if (!uid) return;
    const all = this.loadAllUiState();
    all[id] = state;
    localStorage.setItem(this.uiKey(uid), JSON.stringify(all));
  }

  private clearUiState(id: number): void {
    const uid = this.currentUserId();
    if (!uid) return;
    const all = this.loadAllUiState();
    delete all[id];
    localStorage.setItem(this.uiKey(uid), JSON.stringify(all));
  }

  setOpen(id: number, isOpen: boolean): void {
    this.saveUiState(id, { is_open: isOpen });
    this._notes.next(
      this._notes.value.map(n => n.id === id ? { ...n, is_open: isOpen } : n)
    );
  }

  openAll(): void {
    const uid = this.currentUserId();
    if (!uid) return;
    const ui = this.loadAllUiState();
    this._notes.value.forEach(n => { ui[n.id] = { is_open: true }; });
    localStorage.setItem(this.uiKey(uid), JSON.stringify(ui));
    this._notes.next(this._notes.value.map(n => ({ ...n, is_open: true })));
  }

  closeAll(): void {
    const uid = this.currentUserId();
    if (!uid) return;
    const ui = this.loadAllUiState();
    this._notes.value.forEach(n => { ui[n.id] = { is_open: false }; });
    localStorage.setItem(this.uiKey(uid), JSON.stringify(ui));
    this._notes.next(this._notes.value.map(n => ({ ...n, is_open: false })));
  }

  // ── Clear (called on logout / token expiry) ───────────────────────────────

  clear(userId: number): void {
    this._notes.next([]);
    localStorage.removeItem(this.geoKey(userId));
    localStorage.removeItem(this.uiKey(userId));
  }

  // ── API ───────────────────────────────────────────────────────────────────

  load() {
    return this.http.get<StickyNote[]>(this.url).pipe(
      tap(notes => {
        const uiState = this.loadAllUiState();
        const merged = notes.map((n, i) => {
          const geo = this.getGeometry(n.id);
          const ui  = uiState[n.id];
          const defaultGeo: NoteGeometry = {
            pos_x: 120 + (i % 5) * 30,
            pos_y: 120 + (i % 5) * 30,
            width: 260,
            height: 220,
          };
          return {
            ...n,
            ...(geo ?? defaultGeo),
            is_open: ui?.is_open ?? false,
          };
        });
        this._notes.next(merged);
      })
    );
  }

  create() {
    const count = this._notes.value.length;
    const newZ = Math.max(9100, ...this._notes.value.map(n => n.z_index ?? 9100)) + 1;
  
    return this.http.post<StickyNote>(this.url, {
      color: this.nextColor(count),
      z_index: newZ,
    }).pipe(
      tap(note => {
        const geo: NoteGeometry = {
          pos_x: 120 + (count % 5) * 30,
          pos_y: 120 + (count % 5) * 30,
          width: 260,
          height: 220,
        };
        this.saveGeometry(note.id, geo);
        this.saveUiState(note.id, { is_open: true });
        this._notes.next([{ ...note, ...geo, is_open: true, z_index: newZ }, ...this._notes.value]);
      })
    );
  }
  update(id: number, changes: Partial<StickyNote>) {
    const { pos_x, pos_y, width, height, is_open, ...backendChanges } = changes as any;

    const geoFields: Partial<NoteGeometry> = {};
    if (pos_x  !== undefined) geoFields.pos_x  = pos_x;
    if (pos_y  !== undefined) geoFields.pos_y  = pos_y;
    if (width  !== undefined) geoFields.width  = width;
    if (height !== undefined) geoFields.height = height;
    if (Object.keys(geoFields).length) {
      this.saveGeometry(id, geoFields);
      this._notes.next(this._notes.value.map(n => n.id === id ? { ...n, ...geoFields } : n));
    }

    if (is_open !== undefined) {
      this.setOpen(id, is_open);
    }

    if (!Object.keys(backendChanges).length) {
      return of(this._notes.value.find(n => n.id === id)!);
    }

    return this.http.patch<StickyNote>(`${this.url}/${id}`, backendChanges).pipe(
      tap(updated => this._notes.next(
        this._notes.value.map(n => n.id === id ? { ...n, ...updated } : n)
      )),
      catchError(err => {
        if (err.status === 404) {
          this._notes.next(this._notes.value.filter(n => n.id !== id));
          this.clearGeometry(id);
          this.clearUiState(id);
        }
        return throwError(() => err);
      })
    );
  }

  delete(id: number) {
    return this.http.delete(`${this.url}/${id}`).pipe(
      tap(() => {
        this._notes.next(this._notes.value.filter(n => n.id !== id));
        this.clearGeometry(id);
        this.clearUiState(id);
      }),
      catchError(err => {
        if (err.status === 404) {
          // Note doesn't belong to this user — remove it from local state silently
          this._notes.next(this._notes.value.filter(n => n.id !== id));
          this.clearGeometry(id);
          this.clearUiState(id);
        }
        return throwError(() => err);
      })
    );
  }

  private nextColor(index: number): string {
    const palette = [
      '#FFEE58', '#66BB6A', '#42A5F5', '#EC407A', '#FFA726',
      '#AB47BC', '#78909C', '#EF5350', '#26C6DA', '#9CCC65',
      '#FF7043', '#5C6BC0', '#8D6E63',
    ];
    return palette[index % palette.length];
  }

  bringToFront(id: number): number {
    const maxZ = Math.max(9100, ...this._notes.value.map(n => n.z_index ?? 9100));
    const newZ = maxZ + 1;
  
    // Update in memory immediately
    this._notes.next(
      this._notes.value.map(n => n.id === id ? { ...n, z_index: newZ } : n)
    );
  
    // Persist to backend fire-and-forget
    this.http.patch(`${this.url}/${id}`, { z_index: newZ }).subscribe({
      error: (e) => console.warn('[StickyNotes] Failed to save z_index', e),
    });
  
    return newZ;
  }

  get notes(): StickyNote[] { return this._notes.value; }
  get openCount(): number   { return this._notes.value.filter(n => n.is_open).length; }
  get maxZIndex(): number   { return Math.max(0, ...this._notes.value.map(n => n.z_index)); }
}